import { useMemo } from 'react';
import { useSelectedOrganization } from '../../organization/hooks/useSelectedOrganization';
import { useSelectedLocation } from '../../location-management/hooks/useSelectedLocation';
import { useGetLocations } from '../../location-management/hooks/useGetLocations';
import { useGetCourts } from '../../court-management/hooks/useGetCourts';
import { useListCoachs } from '../../coach-management/hooks/useListCoachs';
import { useGetEvents } from '../../event-management/hooks/useListEvents';
import { classifyRead, ReadGate, ReadState } from '../../_shared/readState';
import { CHECKLIST_ITEMS, ChecklistItemId } from '../constants';
import { useWalkthroughStore } from './useWalkthroughStore';

/**
 * Why a checklist row could not answer — the third state R90-2 asked for.
 *
 * `unreadable` and `never-issued` are kept apart rather than collapsed into one
 * "unknown" flag because the row has to say something different about each, and
 * the two sentences are not interchangeable: "we asked and did not get an
 * answer" is a fault, "nothing has been asked yet" is a missing selection the
 * operator fixes in the navbar. `gate` says WHICH selection, so the row can
 * borrow the right existing string (`scopePromptI18nKey`, D59 — the branch that
 * picks the key lives in `readState.ts` and is not copied here).
 */
export type ChecklistUnknown =
  | { reason: 'unreadable' }
  | { reason: 'never-issued'; gate: ReadGate };

export type ChecklistItemStatus = {
  id: ChecklistItemId;
  completed: boolean;
  loading: boolean;
  /** Non-null when neither `completed: true` nor `completed: false` is a claim we are entitled to make. */
  unknown: ChecklistUnknown | null;
};

type ChecklistRow = Pick<ChecklistItemStatus, 'completed' | 'loading' | 'unknown'>;

/**
 * A checklist row derived from one gated list read.
 *
 * R90-2 (filed run 90, measured to be FOUR rows rather than one at run 113):
 * `completed: (data?.length ?? 0) > 0` with `loading` from the raw `isLoading`
 * has only two outputs, and a read that failed or was never issued lands in the
 * SAME one as a read that answered "none" — the unticked box. So the onboarding
 * checklist told an operator who has courts, coaches, clubs or events that they
 * still need to create some, in the one surface whose whole job is to tell them
 * what is left to do. D57: a UI that cannot say "I don't know" will say
 * something false.
 *
 * The three outputs are mutually exclusive by construction — `classifyRead`
 * returns exactly one of four states — and `completed` is true ONLY on
 * `answered`, which is the half that matters: every other state leaves the row
 * uncompleted AND marked, never uncompleted and silent.
 *
 * Note what `answered` still allows and what it does not. A BACKGROUND refetch
 * that fails keeps `data`, so `isUnreadable` is false and the row stays on the
 * last good answer rather than flipping to a fault (R96-3) — the mirror defect
 * of the one this fixes. A row is `unreadable` only when the read failed with
 * nothing to show.
 */
export function checklistRowFromRead(
  state: ReadState,
  hasRows: boolean,
  gate: ReadGate,
): ChecklistRow {
  switch (state) {
    case 'unreadable':
      return { completed: false, loading: false, unknown: { reason: 'unreadable' } };
    case 'loading':
      return { completed: false, loading: true, unknown: null };
    case 'never-issued':
      return { completed: false, loading: false, unknown: { reason: 'never-issued', gate } };
    case 'answered':
      return { completed: hasRows, loading: false, unknown: null };
  }
}

/** A row whose answer comes from the store rather than from a read: it is always knowable. */
function settledRow(completed: boolean): ChecklistRow {
  return { completed, loading: false, unknown: null };
}

/**
 * Derives checklist item completion entirely from existing React Query / Zustand data.
 * Non-auto-detectable items fall back to manualOverrides from the walkthrough store.
 */
export function useChecklistItems(uid: string | undefined) {
  const { manualOverrides } = useWalkthroughStore(uid);

  const selectedOrganization = useSelectedOrganization.use.selectedOrganization();
  const selectedLocation = useSelectedLocation.use.selectedLocation();

  const orgId = selectedOrganization?.id ?? '';
  const locationId = selectedLocation?.id ?? '';

  const locations = useGetLocations(orgId);
  const courts = useGetCourts(locationId);
  const coaches = useListCoachs(orgId);
  const events = useGetEvents(locationId, 'future');

  const autoCompletionMap: Record<ChecklistItemId, ChecklistRow> = useMemo(
    () => ({
      create_organization: settledRow(!!selectedOrganization),
      // `isInitialLoading` rather than `!!orgId && isLoading` (D62): the
      // conjunct is a COPY of the hook's own `enabled:` expression kept in
      // this file, correct today and silently wrong the day that gate gains
      // a term. The query already knows the answer. The three sibling rows
      // below now take it too — run 107 left them on their restated gates
      // because their hooks were outside its slice, and this run's slice is
      // exactly those hooks.
      add_location: checklistRowFromRead(
        classifyRead(locations.isError, locations.isInitialLoading, locations.data),
        (locations.data?.length ?? 0) > 0,
        'organization',
      ),
      // `useGetCourts` gained `enabled: Boolean(locationId)` in run 134
      // (R110-6, closed), so `never-issued` is now REACHABLE on this row and it
      // was the right shape to have built for. What the gate changed is the
      // answer, not the code: with no club selected this read used to be issued
      // as `courts?locationId=` and answered 200 with `[]`, so the row rendered
      // `answered` + zero rows — telling an operator whose venue is full of
      // courts that they still need to create one. It now renders the
      // never-issued prompt for the location gate below.
      setup_courts: checklistRowFromRead(
        classifyRead(courts.isError, courts.isInitialLoading, courts.data),
        (courts.data?.length ?? 0) > 0,
        'location',
      ),
      connect_stripe: settledRow(!!selectedOrganization?.stripeAccountId),
      add_coaches: checklistRowFromRead(
        classifyRead(coaches.isError, coaches.isInitialLoading, coaches.data),
        (coaches.data?.length ?? 0) > 0,
        'organization',
      ),
      create_event: checklistRowFromRead(
        classifyRead(events.isError, events.isInitialLoading, events.data),
        (events.data?.length ?? 0) > 0,
        'location',
      ),
      create_membership: settledRow(false), // no auto-detection
      create_product: settledRow(false), // no auto-detection
      create_coupons: settledRow(false), // no auto-detection
    }),
    [
      selectedOrganization,
      locations.data, locations.isInitialLoading, locations.isError,
      courts.data, courts.isInitialLoading, courts.isError,
      coaches.data, coaches.isInitialLoading, coaches.isError,
      events.data, events.isInitialLoading, events.isError,
    ],
  );

  const items: ChecklistItemStatus[] = useMemo(
    () =>
      CHECKLIST_ITEMS.map((def) => {
        const auto = autoCompletionMap[def.id];
        // For auto-detect items, use derived state. For manual items, check overrides first.
        const completed = def.autoDetect
          ? auto.completed
          : manualOverrides[def.id] ?? auto.completed;

        return { id: def.id, completed, loading: auto.loading, unknown: auto.unknown };
      }),
    [autoCompletionMap, manualOverrides],
  );

  const completedCount = items.filter((i) => i.completed).length;
  const totalCount = items.length;
  // Unchanged on purpose, and it is the direction that stays safe: an unknown
  // row is never `completed`, so it can only hold `allComplete` false. The
  // celebration card and the one-shot `walkthrough_checklist_completed` event
  // therefore cannot fire off a read that failed or was never issued.
  //
  // What it does NOT fix, filed as R119-2 rather than guessed: the header
  // caption reads "2 / 9 complete" and the bar draws the same ratio, so a row
  // we could not assess is counted with the rows we assessed and found
  // incomplete. It understates rather than overstates, and saying otherwise
  // needs a sentence this campaign may not add — `src/i18n/{en,fr}.ts` are
  // frozen inside open PR #577 (R96-7).
  const allComplete = completedCount === totalCount;

  return { items, completedCount, totalCount, allComplete };
}
